import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Minus, Plus, Trash2, Share2 } from 'lucide-react';
import { Product } from '../../types/Product';
import { useShare } from '../../hooks/useShare';

interface ProductCardProps {
  product: Product;
  quantity: number;
  onRemove: () => void;
  onUpdateQuantity: (quantity: number) => void;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  quantity,
  onRemove,
  onUpdateQuantity
}) => {
  const { handleShare } = useShare();
  const totalPrice = product.current_price * quantity;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      whileHover={{ scale: 1.02 }}
      className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-all group"
    >
      <motion.img
        whileHover={{ scale: 1.1 }}
        src={product.thumbnail}
        alt={product.display_name}
        className="w-16 h-16 object-cover rounded-lg"
      />
      
      <div className="flex-1 min-w-0">
        <h4 className="font-medium line-clamp-1">{product.display_name}</h4>
        <p className="text-sm text-gray-500 line-clamp-1">{product.category_name}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-orange-600 font-semibold">
            {product.current_price.toFixed(2)}€
          </span>
          {product.price_decreased && (
            <motion.span 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-sm text-gray-500 line-through"
            >
              {product.previous_price.toFixed(2)}€
            </motion.span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 bg-orange-50 rounded-lg p-1">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onUpdateQuantity(quantity - 1)}
            disabled={quantity <= 1}
            className="p-1 rounded-full hover:bg-orange-100 disabled:opacity-50 disabled:hover:bg-transparent"
          >
            <Minus size={16} />
          </motion.button>
          
          <AnimatePresence mode="wait">
            <motion.span
              key={quantity}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="w-8 text-center font-medium"
            >
              {quantity}
            </motion.span>
          </AnimatePresence>
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onUpdateQuantity(quantity + 1)}
            className="p-1 rounded-full hover:bg-orange-100"
          >
            <Plus size={16} />
          </motion.button>
        </div>

        <motion.div
          layout
          className="w-24 text-right font-semibold"
        >
          {totalPrice.toFixed(2)}€
        </motion.div>

        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => handleShare(product)}
            className="p-2 text-orange-500 hover:text-orange-600"
          >
            <Share2 size={18} />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onRemove}
            className="p-2 text-red-500 hover:text-red-600"
          >
            <Trash2 size={18} />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};